<script setup>
  import { ref, computed, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import ModalTextInput from '@/components/controls/ModalTextInput.vue';
  import { areEmailsValid, findInvalidEmail } from '@/helpers/validation';

  const { t } = useI18n();

  const emits = defineEmits(['delete']);

  const props = defineProps({
    device: {
      type: Object,
      default: null,
    },
    canDelete: {
      type: Boolean,
      default: false,
    },
  });

  const name = ref('');
  const address = ref(['']);
  const validationErrors = ref({});

  const liveValid = computed(() => ({
    address: areEmailsValid(parseEmailList(address.value)),
  }));

  const errorCount = computed(() => Object.keys(validationErrors.value).length);

  function parseEmailList(value) {
    if (Array.isArray(value)) {
      return value.map(a => String(a).trim()).filter(Boolean);
    }
    return String(value || '').split(',').map(a => a.trim()).filter(Boolean);
  }

  function validate() {
    const errors = {};
    const addresses = parseEmailList(address.value);
    if (!addresses.length) {
      errors.address = t('alertDevices.emailRequired');
    } else {
      const invalid = findInvalidEmail(addresses);
      if (invalid) errors.address = `"${invalid}" is not a valid email address`;
    }
    validationErrors.value = errors;
    return Object.keys(errors).length === 0;
  }

  function buildPayload() {
    return {
      name: name.value,
      address: parseEmailList(address.value).join(','),
    };
  }

  watch(() => props.device, (device) => {
    validationErrors.value = {};
    if (device) {
      name.value = device.name || '';
      const addresses = parseEmailList(device.address);
      address.value = addresses.length ? addresses : [''];
    } else {
      name.value = t('alertDevices.email');
      address.value = [''];
    }
  }, { immediate: true });

  defineExpose({ validate, buildPayload, errorCount });
</script>

<template>
  <div>
    <div class="jwx-modal__section">
      <div class="jwx-modal__section-body">
        <ModalTextInput
          v-model="name"
          id="device-email-name"
          name="device-email-name"
          labelText="Name"
          :subText="t('alertDevices.maxCharacters')"
        />
        <ModalTextInput
          v-model="address"
          id="device-email-address"
          name="device-email-address"
          :labelText="t('alertDevices.emailAddress')"
          :placeholder="t('alertDevices.emailPlaceholder')"
          multi
          required
          :errorText="validationErrors.address"
          :valid="!!liveValid.address"
        />
      </div>
    </div>
    <div v-if="canDelete" class="jwx-modal__section jwx-p-jumbo jwx-flex jwx-justify-center">
      <button
        type="button"
        class="jwx-button jwx-button--pill"
        data-type="danger"
        @click.prevent="emits('delete')"
      >
        {{ t('alertDevices.deleteDevice') }}
      </button>
    </div>
  </div>
</template>
